import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
export interface FloatingMenuItem {
  label: string;
  onSelect?: () => void;
  icon?: ReactNode;
  destructive?: boolean;
}
interface FloatingMenuProps {
  trigger: ReactNode;
  items: FloatingMenuItem[];
  align?: 'left' | 'right';
  /** Etiqueta accesible del botón que abre el menú. */
  label?: string;
}

/**
 * Menú de acciones que se pinta con un portal en `document.body`.
 *
 * Los desplegables normales quedaban recortados dentro de la tarjeta o de la
 * fila de la tabla (contenedores con `overflow` propio). Al usar un portal con
 * posición fija, el menú flota por encima de toda la interfaz, se recoloca solo
 * si no cabe debajo o se sale por un lateral, y se cierra al pulsar fuera, al
 * desplazar la página o con Escape.
 */
export function FloatingMenu({
  trigger,
  items,
  align = 'right',
  label = 'Abrir acciones'
}: FloatingMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const width = 224;
  function place() {
    const box = anchor.current?.getBoundingClientRect();
    if (!box) return;
    const height = Math.min(items.length * 44 + 12, 320);
    const below = window.innerHeight - box.bottom > height + 12;
    const left = align === 'right' ? box.right - width : box.left;
    setPos({
      top: below ? box.bottom + 6 : Math.max(8, box.top - height - 6),
      left: Math.min(Math.max(8, left), window.innerWidth - width - 8)
    });
  }
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (menu.current?.contains(target) || anchor.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    function onScroll() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);
  return <>
      <button ref={anchor} type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={event => {
      event.stopPropagation();
      if (open) {
        setOpen(false);
        return;
      }
      place();
      setOpen(true);
    }} className="inline-flex items-center justify-center" data-id="element-824" data-mp="2qze2c6ndaryq">
        {trigger}
      </button>

      {open && pos && createPortal(<div ref={menu} role="menu" style={{
      top: pos.top,
      left: pos.left,
      width
    }} className="fixed z-[80] overflow-hidden rounded-xl border border-border bg-background py-1.5 shadow-xl" data-id="element-825" data-mp="bacp198ir5ei">
            {items.map(item => <button key={item.label} type="button" role="menuitem" onClick={event => {
        event.stopPropagation();
        setOpen(false);
        item.onSelect?.();
      }} className={`flex min-h-[44px] w-full items-center gap-2.5 px-3.5 text-left text-sm transition-colors hover:bg-surface ${item.destructive ? 'text-red-600' : 'text-foreground'}`} data-id="element-826" data-mp="1m07q84ipw6vb">
                {item.icon}
                {item.label}
              </button>)}
          </div>, document.body)}
    </>;
}